import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  Grid,
  IconButton,
  useTheme,
  Skeleton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import { useGetDashboard, useGetUserById } from 'hooks/useUsers';
import { useAuth } from 'context/AuthContext';
import { colors } from 'styles/colors';
import RVUsIcon from 'icons/RVUsIcon';
import CustomText from 'components/common/CustomText/CustomText';
import LockOutlineIcon from '@mui/icons-material/LockOutline';
import { hexWithOpacity } from 'common/utils';
import { useGetCurrentPlan } from 'hooks/usePlans';
import { snackbarService } from 'services/snackbarService';
import UpgradeDialog from 'components/dialogs/UpgradeDialog';

interface CardProps {
  icon: React.ReactNode;
  color: string;
  value?: React.ReactNode;
  text?: string;
  content?: React.ReactNode;
  status?: string;
}

const RVUCard: React.FC<CardProps> = ({ icon, status, value, text, content }) => {
  const theme = useTheme();
  const color = colors.white;

  return (
    <Card
      variant="outlined"
      sx={{
        backgroundColor: color,
        borderColor: hexWithOpacity(color, 0.5),
        height: 170,
        borderRadius: '15px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box
            sx={{
              backgroundColor: colors.topBackground,
              display: 'inline-flex',
              height: 42,
              width: 37,
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '8px',
            }}
          >
            {icon}
          </Box>
          {status && <CustomText text={status} fontSize={12} color={colors.primary} />}
        </Box>

        {text ? (
          <CustomText text={text} mt={1} fontSize={15} color={colors.secondary} />
        ) : (
          content
        )}

        {value !== null && value !== undefined && (
          <CustomText
            text={value.toString()}
            mt={1}
            fontSize={35}
            color={colors.primary}
            fontWeight={600}
          />
        )}

      </CardContent>
    </Card>
  );
};

const UpgradeCard: React.FC<{ color: string; text?: string; onUpgradeClick?: () => void }> = ({
  text,
  onUpgradeClick,
}) => {
  const theme = useTheme();
  const color = colors.white;

  return (
    <Card
      variant="outlined"
      sx={{
        backgroundColor: color,
        borderColor: hexWithOpacity(color, 0.5),
        height: 170,
        borderRadius: '15px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box
              sx={{
                backgroundColor: colors.topBackground,
                display: 'inline-flex',
                height: 42,
                width: 37,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
              }}
            >
              <LockOutlineIcon sx={{ color: colors.primary }} />
            </Box>
          </Box>

          {text && (
            <CustomText text={text} mt={1} fontSize={15} color={colors.secondary} />
          )}
        </Box>

        <Box sx={{ mt: 'auto' }}>
          {/* <CustomText text="Please upgrade your plan to unlock this" /> */}
          <Box mt={1} fontSize={15} color={colors.black}>
            Please{' '}
            <span
              onClick={onUpgradeClick}
              style={{
                color: colors.link,
                cursor: 'pointer',
              }}
            >
              upgrade
            </span>{' '}
            your plan to unlock this
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const SkeletonCard = () => (
  <Card
    variant="outlined"
    sx={{
      backgroundColor: colors.white,
      borderColor: hexWithOpacity(colors.white, 0.5),
      height: 170,
      borderRadius: '15px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.10)',
    }}
  >
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Skeleton variant="rectangular" width={37} height={42} sx={{ borderRadius: 2, backgroundColor: colors.skeleton }} />
        <Skeleton width={80} height={16} sx={{ backgroundColor: colors.skeleton }} />
      </Box>

      <Skeleton width="80%" height={20} sx={{ mt: 1, backgroundColor: colors.skeleton }} />
      <Skeleton width="60%" height={40} sx={{ mt: 2, backgroundColor: colors.skeleton }} />
    </CardContent>
  </Card>
);

export default function RVUs() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rvusExpanded, setRvusExpanded] = useState(false);

  const { data: userInfo, refetch: refetchUser } = useGetUserById(user.id);
  const { data, refetch: refetchDashboard } = useGetDashboard();
  const { data: currentPlan, refetch: refetchCurrentPlan } = useGetCurrentPlan();
  const [openUpgrade, setOpenUpgrade] = useState<boolean>(false);

  const openUpgradeDialog = () => {
    if (currentPlan?.data?.is_subscription_cancelled) {
      snackbarService.showSnackbar("You can't manage your plans since you've cancelled your current subscription.", 'info', 5000);
    } else {
      setOpenUpgrade(true);
    }
  }

  const navigateToProfileReimbursement = () => {
    navigate('/profile', {
      state: {
        tab: 'reimbursement'
      }
    })
  }

  useEffect(() => {
    refetchUser();
    refetchDashboard();
  }, [refetchUser, refetchDashboard]);

  const isFreePlan = useMemo(() => {
    return currentPlan?.data?.plan?.name === 'Free';
  }, [currentPlan])

  const estimatedReimbursement = useMemo(() => {
    if (!userInfo?.reimbursement) return 0;

    const r = userInfo.reimbursement;

    if (r.reimbursement_model === 'SALARY_BASED') return 'Salary Based';

    if (r.reimbursement_model === 'RVUS_BASED') {
      return data?.total_reimbursable * r.rvus_multiplier;
    }

    if (r.reimbursement_model === 'MIXED') {
      if (r.threshold === 'DOLLARS') {
        return data?.total_reimbursable * r.rvus_multiplier - r.amount;
      }
      if (r.threshold === 'RVUS') {
        return (data?.total_reimbursable - r.amount) * r.rvus_multiplier;
      }
    }

    return 0;
  }, [userInfo, data]);

  const formattedReimbursement = useMemo(() => {
    if (typeof estimatedReimbursement !== 'number') return estimatedReimbursement;
    const abs = Math.abs(estimatedReimbursement).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${estimatedReimbursement < 0 ? '-$' : '$'}${abs}`;
  }, [estimatedReimbursement]);

  const isAboveBaseSalary = useMemo(() => {
    return (
      typeof estimatedReimbursement === 'number' &&
      estimatedReimbursement > 0 &&
      userInfo?.reimbursement?.reimbursement_model == 'MIXED'
    );
  }, [userInfo]);

  const isThresholdNotMet = useMemo(() => {
    return (
      typeof estimatedReimbursement === 'number' &&
      estimatedReimbursement < 0 &&
      userInfo?.reimbursement?.reimbursement_model == 'MIXED'
    );
  }, [userInfo]);

  const reimbursementStatus = useMemo(() => {
    return isAboveBaseSalary
      ? '(Above Base Salary)'
      : isThresholdNotMet
        ? '(Threshold Not Met)'
        : '';
  }, [isAboveBaseSalary, isThresholdNotMet]);

  if (!data || !userInfo) {
    return (
      <Box>
        {/* Always-visible skeleton row */}
        <Grid container spacing={3}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Grid key={index} size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
              <SkeletonCard />
            </Grid>
          ))}
        </Grid>

        {/* Collapsed toggle skeleton */}
        <Box display="flex" alignItems="center" justifyContent="center" mt={1.4}>
          <Skeleton width={150} height={20} sx={{ backgroundColor: colors.skeleton }} />
        </Box>

        {/* Matches the mb: -2 on the real collapse wrapper so height is identical */}
        <Box sx={{ mb: -2 }} />
      </Box>
    );
  }

  return (
    <>
      <Box>
        {/* Always-visible: Number of Cases row */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
            <RVUCard
              icon={<RVUsIcon color={colors.primary} />}
              color={theme.palette.primary.main}
              value={data.procedures_within_this_month + data.case_procedures_within_this_month}
              text="Number of Cases in this Month"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
            <RVUCard
              icon={<RVUsIcon color={colors.primary} />}
              color={theme.palette.primary.main}
              value={data.procedures_within_academic_year + data.case_procedures_within_academic_year}
              text="Number of Cases this Year"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4, lg: 4 }}>
            <RVUCard
              icon={<RVUsIcon color={colors.primary} />}
              color={theme.palette.primary.main}
              value={data.procedures_count + data.case_procedures_count}
              text="Total Number of Cases"
            />
          </Grid>
        </Grid>

        {/* Collapsible toggle: RVUs & Reimbursement */}
        <Box
          display="flex"
          alignItems="center"
          mt={1.4}
          sx={{ cursor: 'pointer', userSelect: 'none', width: 'fit-content', mx: 'auto' }}
          onClick={() => setRvusExpanded((prev) => !prev)}
        >
          <CustomText
            text="RVUs & Reimbursement"
            fontSize={13}
            fontWeight={600}
            color={colors.primary}
          />
          <IconButton
            size="small"
            disableRipple
            sx={{
              mr: 0.5,
              mb: 0.1,
              p: 0,
              color: colors.primary,
              transform: rvusExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Collapsible: RVUs & Reimbursement cards */}
        {/* mx/mb negative offset compensates for the inner padding that gives box-shadow room inside overflow:hidden */}
        <Box sx={{ mx: -1.5, mb: -2 }}>
          <Collapse in={rvusExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ pt: 1, px: 1.5, pb: 2 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
                  {isFreePlan ? (
                    <UpgradeCard
                      color={theme.palette.warning.main}
                      text="Estimated Reimbursable RVUs this Month"
                      onUpgradeClick={openUpgradeDialog}
                    />
                  ) : (
                    <RVUCard
                      icon={<RVUsIcon color={colors.primary} />}
                      color={theme.palette.warning.main}
                      value={data.rvu_within_this_month.toFixed(2)}
                      text="Estimated Reimbursable RVUs this Month"
                    />
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
                  {isFreePlan ? (
                    <UpgradeCard
                      color={theme.palette.success.main}
                      text="Estimated Reimbursable RVUs this Year"
                      onUpgradeClick={openUpgradeDialog}
                    />
                  ) : (
                    <RVUCard
                      icon={<RVUsIcon color={colors.primary} />}
                      color={theme.palette.success.main}
                      value={data.total_reimbursable.toFixed(2)}
                      text="Estimated Reimbursable RVUs this Year"
                    />
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
                  {isFreePlan ? (
                    <UpgradeCard
                      color={theme.palette.error.main}
                      text="Estimated Reimbursement for this Year"
                      onUpgradeClick={openUpgradeDialog}
                    />
                  ) : (
                    <RVUCard
                      icon={<RVUsIcon color={colors.primary} />}
                      color={theme.palette.error.main}
                      value={formattedReimbursement}
                      content={
                        <Box mt={1} fontSize={15} color={colors.secondary}>
                          Estimated{' '}
                          <span
                            onClick={navigateToProfileReimbursement}
                            style={{
                              color: colors.link,
                              cursor: 'pointer',
                            }}
                          >
                            Reimbursement
                          </span>{' '}
                          for this Year
                        </Box>
                      }
                      status={reimbursementStatus}
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Box>
      </Box>

      <UpgradeDialog
        open={openUpgrade}
        setOpen={setOpenUpgrade}
        refetch={refetchCurrentPlan}
      />
    </>
  );
}
